import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertCircle,
  RefreshCw,
  Copy,
  RotateCcw,
  Send,
  ImagePlus,
  ExternalLink,
  Eye,
  CheckCheck,
  Inbox,
  KeyRound,
  Loader2,
  Trash2,
  Image as ImageIcon,
  QrCode,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { QrCodeBlock } from '@/components/common/QrCodeBlock';
import {
  loadAllClientPortalData,
  ensureCodesForClients,
  regenerateCode,
  setPortalActive,
  buildPortalLink,
  sendMessage,
  markMessagesRead,
  flattenInbox,
  sharePhotos,
  removeSharedPhoto,
  formatUsd,
} from '@/lib/clientPortalData';
import { toast } from 'sonner';

const doCopy = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* noop */ }
  return false;
};

/* -------------------------------------------------------------------------
 * Codes List
 * ----------------------------------------------------------------------- */
const CodesList = ({ clients, portals, onRegen, onToggle, onPreview, onMessage, onShowQr }) => {
  const rows = (clients || []).map((c) => ({
    client: c,
    config: portals?.[c.id] || {},
  }));
  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="client-portal-codes">
      <CardContent className="p-3 sm:p-4">
        <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
          <span className="col-span-4">Client</span>
          <span className="col-span-2">Code</span>
          <span className="col-span-3">Portal link</span>
          <span className="col-span-1">Active</span>
          <span className="col-span-2 text-right">Actions</span>
        </div>
        {rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="client-portal-codes-empty">
            No clients yet. Add a client first.
          </div>
        ) : (
          rows.map(({ client, config }) => (
            <div
              key={client.id}
              className="grid grid-cols-12 gap-2 items-center px-3 py-3 border-b border-border"
              data-testid={`client-portal-row-${client.id}`}
            >
              <div className="col-span-12 sm:col-span-4 min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">{client.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {client.email || client.phone || 'No contact'}
                </div>
              </div>
              <div className="col-span-4 sm:col-span-2">
                <Badge
                  variant="outline"
                  className="font-mono tabular-nums text-sm tracking-[0.4em] border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 px-2.5 py-1"
                  data-testid={`client-portal-code-${client.id}`}
                >
                  {config.code || '\u2014'}
                </Badge>
              </div>
              <div className="col-span-8 sm:col-span-3 truncate">
                <span className="text-[11px] text-muted-foreground font-mono truncate block">
                  {config.code ? buildPortalLink(config.code) : 'No code yet'}
                </span>
              </div>
              <div className="col-span-4 sm:col-span-1">
                <Switch
                  checked={!!config.active}
                  onCheckedChange={(v) => onToggle(client.id, v)}
                  data-testid={`client-portal-active-${client.id}`}
                />
              </div>
              <div className="col-span-8 sm:col-span-2 flex items-center justify-end gap-1.5 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    const link = buildPortalLink(config.code);
                    const ok = await doCopy(`${link} (Code: ${config.code})`);
                    if (ok) toast.success('Portal link + code copied');
                    else toast.error('Could not access clipboard');
                  }}
                  disabled={!config.code}
                  className="h-8 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-[10px]"
                  data-testid={`client-portal-copy-${client.id}`}
                >
                  <Copy size={11} className="mr-1" />
                  Copy
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onPreview(config.code)}
                  disabled={!config.code}
                  className="h-8 text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] uppercase tracking-[0.16em] text-[10px]"
                  data-testid={`client-portal-preview-${client.id}`}
                >
                  <Eye size={11} className="mr-1" />
                  Preview
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onShowQr(client, config)}
                  disabled={!config.code}
                  className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                  aria-label="Show QR code"
                  data-testid={`client-portal-qr-${client.id}`}
                >
                  <QrCode size={12} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onMessage(client.id)}
                  className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                  aria-label="Message"
                  data-testid={`client-portal-message-${client.id}`}
                >
                  <Send size={12} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onRegen(client.id)}
                  className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/10"
                  aria-label="Regenerate code"
                  data-testid={`client-portal-regen-${client.id}`}
                >
                  <RotateCcw size={12} />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

/* -------------------------------------------------------------------------
 * Inbox
 * ----------------------------------------------------------------------- */
const InboxView = ({ inbox, activeId, onSelect, onSendReply, onMarkRead, sending }) => {
  const active = inbox.find((r) => r.client_id === activeId) || null;
  const [reply, setReply] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (active.unread > 0) onMarkRead(active.client_id);
    setReply('');
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, active?.messages?.length]);

  const handleSend = async () => {
    if (!active) return;
    if (!reply.trim()) return;
    await onSendReply(active.client_id, reply.trim());
    setReply('');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="client-portal-inbox">
      {/* List */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="flex items-center gap-2">
              <Inbox size={14} className="text-[hsl(var(--primary))]" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Inbox
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {inbox.length} threads
            </span>
          </div>
          {inbox.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No messages yet.
            </div>
          ) : (
            <div className="space-y-1" data-testid="client-portal-inbox-list">
              {inbox.map((row) => (
                <button
                  key={row.client_id}
                  type="button"
                  onClick={() => onSelect(row.client_id)}
                  data-testid={`client-portal-inbox-row-${row.client_id}`}
                  className={cn(
                    'w-full text-left rounded-md border px-3 py-2 transition-colors',
                    row.client_id === activeId
                      ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5'
                      : 'border-border bg-[hsl(var(--muted))]/40 hover:border-[hsl(var(--primary))]/30'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-foreground truncate">
                      {row.client_name}
                    </span>
                    {row.unread > 0 ? (
                      <Badge
                        variant="outline"
                        className="uppercase text-[9px] tracking-[0.16em] border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10"
                      >
                        {row.unread} new
                      </Badge>
                    ) : null}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {row.last_from === 'client' ? 'Client: ' : 'You: '}{row.last_body}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Thread */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] lg:col-span-2">
        <CardContent className="p-3 sm:p-4">
          {!active ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              Select a thread to read and reply.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs font-semibold text-foreground">{active.client_name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    Code {active.code} {'\u00b7'} {active.total} message{active.total === 1 ? '' : 's'}
                  </div>
                </div>
                {active.unread > 0 ? (
                  <Badge
                    variant="outline"
                    className="uppercase text-[9px] tracking-[0.16em] border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10"
                  >
                    {active.unread} unread
                  </Badge>
                ) : null}
              </div>
              <div
                ref={scrollRef}
                className="space-y-2 max-h-[420px] overflow-auto kc-scroll pr-1 mb-3"
                data-testid="client-portal-thread"
              >
                {active.messages.map((m) => (
                  <div
                    key={m.id}
                    className={cn(
                      'rounded-md border px-3 py-2 max-w-[80%]',
                      m.from === 'contractor'
                        ? 'ml-auto border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10'
                        : 'border-border bg-[hsl(var(--muted))]/40'
                    )}
                  >
                    <div className="text-sm text-foreground whitespace-pre-wrap">{m.body}</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {m.from === 'contractor' ? 'You' : active.client_name} {'\u00b7'}{' '}
                      {m.sent_at ? new Date(m.sent_at).toLocaleString() : ''}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <Textarea
                  rows={2}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={'Reply to client\u2026'}
                  className="flex-1 bg-[hsl(var(--muted))] border-border text-foreground resize-none"
                  data-testid="client-portal-reply-input"
                />
                <Button
                  type="button"
                  onClick={handleSend}
                  disabled={sending || !reply.trim()}
                  className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                  data-testid="client-portal-reply-send"
                >
                  {sending ? <Loader2 size={12} className="mr-2 animate-spin" /> : <Send size={12} className="mr-2" />}
                  Send
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Photo Sharing
 * ----------------------------------------------------------------------- */
const PhotoShare = ({ clients, photos, onUpload, onRemove }) => {
  const fileRef = useRef(null);
  const [pickedClient, setPickedClient] = useState('');
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState('');

  const handleFiles = async (files) => {
    if (!pickedClient) {
      toast.error('Pick a client first.');
      return;
    }
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const fileList = Array.from(files).slice(0, 6);
      // Pass raw Files to the data layer; it will upload to Supabase Storage
      // and persist only the public URLs.
      const result = await onUpload(pickedClient, { files: fileList, caption });
      setCaption('');
      if (fileRef.current) fileRef.current.value = '';
      const n = result?.uploaded ?? fileList.length;
      const failed = result?.failed ?? 0;
      if (failed > 0) {
        toast.warning(`Shared ${n} photo${n === 1 ? '' : 's'}, ${failed} failed.`);
      } else {
        toast.success(`Shared ${n} photo${n === 1 ? '' : 's'}`);
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to share photos.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="client-portal-photos-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Share with client
            </div>
            <Select value={pickedClient} onValueChange={setPickedClient}>
              <SelectTrigger
                className="h-10 bg-[hsl(var(--muted))] border-border mt-1"
                data-testid="client-portal-photo-client"
              >
                <SelectValue placeholder="Pick a client" />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                {(clients || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Caption (optional)
            </div>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Day 3 progress"
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground mt-1"
              data-testid="client-portal-photo-caption"
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="client-portal-photo-file-input"
          />
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={!pickedClient || busy}
            className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="client-portal-photo-upload"
          >
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ImagePlus size={14} className="mr-2" />}
            Push photos
          </Button>
        </div>

        {/* Per-client galleries */}
        <div className="space-y-4" data-testid="client-portal-photo-galleries">
          {Object.entries(photos || {}).filter(([, arr]) => Array.isArray(arr) && arr.length > 0).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              No photos shared yet. Push photos to make them visible to a specific client.
            </div>
          ) : (
            Object.entries(photos || {}).map(([cid, arr]) => {
              if (!Array.isArray(arr) || arr.length === 0) return null;
              const client = (clients || []).find((c) => c.id === cid);
              return (
                <div key={cid} className="border border-border rounded-md bg-[hsl(var(--muted))]/40 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-foreground">
                      {client?.name || 'Unknown client'}
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {arr.length} photo{arr.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                    {arr.map((p) => (
                      <div key={p.id} className="relative group">
                        <img
                          src={p.url}
                          alt={p.caption || 'Shared photo'}
                          className="w-full h-20 object-cover rounded border border-border"
                        />
                        <button
                          type="button"
                          onClick={() => onRemove(cid, p.id)}
                          aria-label="Remove photo"
                          className="absolute top-1 right-1 h-6 w-6 rounded bg-black/60 text-[hsl(var(--destructive))] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          data-testid={`client-portal-photo-remove-${p.id}`}
                        >
                          <Trash2 size={11} />
                        </button>
                        {p.caption ? (
                          <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 bg-black/70 text-[9px] text-white truncate rounded-b">
                            {p.caption}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/* -------------------------------------------------------------------------
 * Compose Dialog
 * ----------------------------------------------------------------------- */
const ComposeDialog = ({ open, onOpenChange, clientId, clients, onSend }) => {
  const client = (clients || []).find((c) => c.id === clientId);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setBody('');
  }, [open]);
  const handle = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await onSend(clientId, body.trim());
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  if (!clientId) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))] max-w-md"
        data-testid="client-portal-compose-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-foreground tracking-tight">
            Message {client?.name || 'client'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {'They\u2019ll see this in the Messages tab of their portal.'}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={'Type your message\u2026'}
          className="bg-[hsl(var(--muted))] border-border text-foreground resize-none"
          data-testid="client-portal-compose-body"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handle}
            disabled={busy || !body.trim()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="client-portal-compose-send"
          >
            {busy ? <Loader2 size={12} className="mr-2 animate-spin" /> : <Send size={12} className="mr-2" />}
            Send message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------------------------------------------------------------
 * Page
 * ----------------------------------------------------------------------- */
export default function ClientPortalPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [clients, setClients] = useState([]);
  const [portals, setPortals] = useState({});
  const [messages, setMessages] = useState({});
  const [photos, setPhotos] = useState({});
  const [signatures, setSignatures] = useState({});

  const [activeInboxId, setActiveInboxId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeClient, setComposeClient] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const data = await loadAllClientPortalData();
      setClients(data.clients);
      const ensured = await ensureCodesForClients(data.clients, data.portals);
      setPortals(ensured);
      setMessages(data.messages);
      setPhotos(data.photos);
      setSignatures(data.signatures);
    } catch (e) {
      setError(e?.message || t('clientPortalAdmin.loadError'));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const inbox = useMemo(() => flattenInbox(messages, portals), [messages, portals]);

  const handleRegen = async (clientId) => {
    try {
      const next = await regenerateCode(clientId, portals);
      setPortals(next);
      toast.success('New code generated');
    } catch (e) {
      toast.error(e?.message || 'Failed to regenerate code.');
    }
  };

  const handleToggle = async (clientId, active) => {
    try {
      const next = await setPortalActive(clientId, active, portals);
      setPortals(next);
      toast.success(active ? 'Portal enabled' : 'Portal disabled');
    } catch (e) {
      toast.error(e?.message || 'Failed to update portal.');
    }
  };

  const handlePreview = (code) => {
    if (!code) return;
    window.open(`/portal/client/${code}`, '_blank', 'noopener,noreferrer');
  };

  const [qrOpen, setQrOpen] = useState(false);
  const [qrTarget, setQrTarget] = useState(null);
  const handleShowQr = (client, config) => {
    if (!config?.code) return;
    setQrTarget({ client, config });
    setQrOpen(true);
  };

  const handleStartMessage = (clientId) => {
    setComposeClient(clientId);
    setComposeOpen(true);
  };

  const handleSendCompose = async (clientId, body) => {
    setSending(true);
    try {
      const next = await sendMessage({ clientId, from: 'contractor', body }, messages);
      setMessages(next);
      toast.success('Message sent');
      setActiveInboxId(clientId);
    } catch (e) {
      toast.error(e?.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleReply = async (clientId, body) => {
    setSending(true);
    try {
      const next = await sendMessage({ clientId, from: 'contractor', body }, messages);
      setMessages(next);
      toast.success('Reply sent');
    } catch (e) {
      toast.error(e?.message || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleMarkRead = async (clientId) => {
    try {
      const next = await markMessagesRead(clientId, 'client', messages);
      setMessages(next);
    } catch (e) {
      // silent
    }
  };

  const handleUploadPhotos = async (clientId, payload) => {
    // payload = { files: File[], caption?: string }
    const { next, uploaded, failed } = await sharePhotos(
      { clientId, files: payload?.files || [], caption: payload?.caption || '' },
      photos
    );
    setPhotos(next);
    return { uploaded, failed };
  };

  const handleRemovePhoto = async (clientId, photoId) => {
    try {
      const next = await removeSharedPhoto({ clientId, photoId }, photos);
      setPhotos(next);
      toast.success('Photo removed');
    } catch (e) {
      toast.error(e?.message || 'Failed to remove photo.');
    }
  };

  if (loading) {
    return (
      <div data-testid="client-portal-page-loading" className="space-y-3">
        <PageHeader
          eyebrow={t('clientPortalAdmin.eyebrow')}
          title={t('clientPortalAdmin.title')}
          subtitle={t('clientPortalAdmin.subtitle')}
        />
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
            <CardContent className="p-4 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="client-portal-page">
      <PageHeader
        eyebrow={t('clientPortalAdmin.eyebrow')}
        title={t('clientPortalAdmin.title')}
        subtitle={t('clientPortalAdmin.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="client-portal-refresh"
          >
            <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('clientPortalAdmin.refresh')}
          </Button>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="client-portal-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <Tabs defaultValue="codes" className="w-full" data-testid="client-portal-tabs">
        <TabsList className="bg-[hsl(var(--muted))] border border-border mb-4 overflow-x-auto no-scrollbar">
          <TabsTrigger value="codes" className="text-xs uppercase tracking-[0.16em]">
            <KeyRound size={12} className="mr-1.5" />
            Codes
          </TabsTrigger>
          <TabsTrigger value="inbox" className="text-xs uppercase tracking-[0.16em]">
            <Inbox size={12} className="mr-1.5" />
            Inbox {inbox.reduce((s, r) => s + (r.unread || 0), 0) > 0 ? `(${inbox.reduce((s, r) => s + (r.unread || 0), 0)})` : ''}
          </TabsTrigger>
          <TabsTrigger value="photos" className="text-xs uppercase tracking-[0.16em]">
            <ImageIcon size={12} className="mr-1.5" />
            Photo sharing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="codes">
          <CodesList
            clients={clients}
            portals={portals}
            onRegen={handleRegen}
            onToggle={handleToggle}
            onPreview={handlePreview}
            onMessage={handleStartMessage}
            onShowQr={handleShowQr}
          />
        </TabsContent>

        <TabsContent value="inbox">
          <InboxView
            inbox={inbox}
            activeId={activeInboxId}
            onSelect={setActiveInboxId}
            onSendReply={handleReply}
            onMarkRead={handleMarkRead}
            sending={sending}
          />
        </TabsContent>

        <TabsContent value="photos">
          <PhotoShare
            clients={clients}
            photos={photos}
            onUpload={handleUploadPhotos}
            onRemove={handleRemovePhoto}
          />
        </TabsContent>
      </Tabs>

      <ComposeDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        clientId={composeClient}
        clients={clients}
        onSend={handleSendCompose}
      />

      {/* QR code dialog ----------------------------------- */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent
          className="bg-[hsl(var(--card))] border-border max-w-md"
          data-testid="client-portal-qr-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <QrCode className="h-4 w-4 text-[hsl(var(--accent))]" />
              Client portal QR
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {qrTarget?.client?.name
                ? `Show this to ${qrTarget.client.name} — they scan it to land on their portal.`
                : 'Show this on-site so the client can scan and access their portal.'}
            </DialogDescription>
          </DialogHeader>
          {qrTarget?.config?.code ? (
            <QrCodeBlock
              value={`${typeof window !== 'undefined' ? window.location.origin : ''}/portal/client/${qrTarget.config.code}`}
              title="Portal access link"
              subtitle={`Code: ${qrTarget.config.code} \u00b7 No login required`}
              downloadName={`portal-qr-${(qrTarget?.client?.name || 'client').replace(/\s+/g, '-').toLowerCase()}.png`}
              size={200}
              testId="client-portal-qr"
            >
              <div className="text-xs text-muted-foreground space-y-1 break-all">
                <div className="text-foreground font-medium">
                  {qrTarget?.client?.name || 'Client'}
                </div>
                <div className="font-mono">
                  /portal/client/{qrTarget.config.code}
                </div>
              </div>
            </QrCodeBlock>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
